import { useFetchData } from 'app/hooks/store';
import { MatchEntity } from 'app/interfaces/entity';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSocketSubscriber } from 'app/hooks/socket';
import { SocketClientMessage } from '@okletsplay/core';

import { useDispatch } from 'react-redux';
import { ActionCreatorWithPayload, AsyncThunkAction } from '@reduxjs/toolkit';
import { Pagination } from 'app/interfaces';

type PaginationReducer = {
  updatePagination: ActionCreatorWithPayload<{ data: Partial<Pagination>; identifier?: string }>;
};

export const usePaginatedList = (
  actions: AsyncThunkAction<unknown, unknown, Record<string, unknown>>[],
  paginationReducer: PaginationReducer,
  limit: number,
  socketClientMessage?: SocketClientMessage,
  paginationKey?: string
) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const { fetch } = useFetchData<MatchEntity[]>(actions, { fetchOnInit: false });

  const updatePagination = useCallback(
    (reload: boolean) => {
      if (paginationKey) {
        dispatch(
          paginationReducer.updatePagination({
            identifier: paginationKey,
            data: { limit, reload },
          })
        );
      } else {
        dispatch(paginationReducer.updatePagination({ data: { limit, reload } }));
      }
    },
    [dispatch, limit, paginationKey, paginationReducer]
  );

  const loadMoreHandler = useCallback(() => {
    setLoadingMore(true);
    updatePagination(false);
    fetch().finally(() => setLoadingMore(false));
  }, [updatePagination, fetch]);

  const reloadHandler = useCallback(() => {
    setLoadingMore(true);
    updatePagination(true);
    fetch().finally(() => setLoadingMore(false));
  }, [updatePagination, fetch]);

  useEffect(() => {
    if (Boolean(limit)) {
      setLoading(true);
      updatePagination(true);
      fetch().finally(() => setLoading(false));
    }
  }, [limit, setLoading, fetch, paginationKey, paginationReducer, updatePagination]);

  const socketActions = useMemo(() => {
    if (!socketClientMessage) {
      return [];
    }

    return [{ action: socketClientMessage, handler: () => fetch(false) }];
  }, [fetch, socketClientMessage]);

  useSocketSubscriber(socketActions);

  return {
    loadMoreHandler,
    reloadHandler,
    loadingMore,
    loading,
  };
};
